// Claim SPToken for all participants

import {useState, useEffect, useRef} from "react";
import {
	Box,
	useToast,
	Input,
	Select,
	Divider,
	InputRightElement,
	InputGroup,
	Button,
	Link,
} from "@chakra-ui/react";
import {
	shortAddress,
	getAllSPTokens,
	getTokenBs,
	getBalance,
	fromWei,
	toWei,
	format,
	getPair,
	getSlippage,
	getLpAmountInPool,
	getDappLpAmountOfMine,
	getPotentialSPTokenBonus,
	getCurrentPrice,
	getLiquidityData,
	removeLiquidity,
	getAllowance,
	approve,
	addLiquidity,
	getMyLPtokens,
	getEstimateLpTokens
} from "../../utils/common";
import {
	ETHER_SCAN_URL,
	SPTOKEN_VAULT,
	ZERO_ADDRESS,
	BASE_API_URL,
	CHAIN_ID,
	NFT_ADDRESS
} from "../../config";
import date from "date-and-time";
import { useAuth } from "../../contexts/AuthContext";
import ConfirmationProgress from "../../components/ConfirmationProgress";
import intl from "../../utils/intl";
import { ethers } from "ethers";

const LiquidityMain = () => {
	const toast = useToast();
  const { currentAccount, currentNetwork } = useAuth();

	const [ hiddenConfirmationProgress, setHiddenConfirmationProgress ] = useState(true);
	const [ confirmationProgressData, setConfirmationProgressData ] = useState({value: 5, message: intl.get("start"), step: 1});

	const [ spTokens, setSPTokens ] = useState([]);
	const [ tokenAAddress, setTokenAAddress] = useState("");
	const [ tokenASymbol, setTokenASymbol ] = useState("");
	const [ tokenAAmount, setTokenAAmount ] = useState(0);
	const [ tokenABalance, setTokenABalance ] = useState(0);
	const [ tokenAId, setTokenAId ] = useState(0);

	const [ tokenBs, setTokenBs] = useState([]);
	const [ tokenBAddress, setTokenBAddress] = useState("");
	const [ tokenBSymbol, setTokenBSymbol ] = useState("");
	const [ tokenBDirection, setTokenBDirection ] = useState(true);
	const [ tokenBAmount, setTokenBAmount ] = useState(0);
	const [ tokenBBalance, setTokenBBalance ] = useState(0);
	const [ tokenBId, setTokenBId ] = useState(0);

	const [ lpToken, setLpToken ] = useState(ZERO_ADDRESS);
	const [ slippage, setSlippage ] = useState(0);
	const [ lpAmountInPool, setLpAmountInPool ] = useState(0);
	const [ lpAmountOfMine, setLpAmountOfMine ] = useState({amount: 0, timestamp: 0, frozenTime: 0, tokenA: '', amountA: ethers.BigNumber.from('0'), tokenB: '', amountB: ethers.BigNumber.from('0')});
	const [ sptAmountInPool, setSptAmountInPool ] = useState(0);
	const [ myPotentialBonus, setMyPotentialBonus ] = useState([ethers.BigNumber.from('0'), '']);
	const [ price, setPrice ] = useState({priceAtoB: 0, priceBtoA: 0});
	const [ frozenTime, setFrozenTime ] = useState(0);
	const [ myLPTokens, setMyLPTokens ] = useState([]);
	const [ estimateLpTokens, setEstimateLpTokens ] = useState(ethers.BigNumber.from('0'));
	
	const [ addLiquidityButtonAvailable, setAddLiquidityButtonAvailable ] = useState(false);
	const [ removeLiquidityButtonAvailable, setRemoveLiquidityButtonAvailable ] = useState(false);

	const loaded = useRef(false);

	// updateFrozenDurations([10*60, 15*24*3600, 90*24*3600, 180*24*3600, 365*24*3600, 720*24*3600]);
	const frozenTimes = [{
		value: 0,
		label: intl.get("select_frozen_time")
	}, 
	// ###### Only for testing
	// {
	// 	value: 10 * 60,
	// 	label: 'Only for testing, 10 minutes'
	// }, 
	{
		value: 15 * 24 * 3600,
		label: '15 Days'
	}, {
		value: 90 * 24 * 3600,
		label: '90 Days'
	}, {
		value: 180 * 24 * 3600,
		label: '180 Days'
	}, {
		value: 360 * 24 * 3600,
		label: '360 Days'
	}, {
		value: 720 * 24 * 3600,
		label: '720 Days'
	}];

	useEffect(() => {
		if(frozenTime > 0 && tokenAAmount > 0 && tokenBAmount > 0 
			&& tokenAAmount <= fromWei(tokenABalance.toString()) * 1 
			&& tokenBAmount <= fromWei(tokenBBalance.toString()) * 1) setAddLiquidityButtonAvailable(true);
		else setAddLiquidityButtonAvailable(false);
	}, [frozenTime, tokenAAmount, tokenBAmount]);

	useEffect(() => {
		setRemoveLiquidityButtonAvailable(lpAmountOfMine.amount > 0 && lpAmountOfMine.frozenTime * 1000 < (new Date().getTime()));
	}, [lpAmountOfMine]);

	useEffect(() => {
		if(!loaded.current && currentAccount !== undefined) {
			loadData();
			return() => loaded.current = true;	
		}
	}, [currentAccount, currentNetwork])

	const getPrice = async (amount) => {
		if(tokenAAddress === '' || tokenBAddress === '' || lpToken === ZERO_ADDRESS || lpAmountOfMine.amount * 1 === 0) setPrice({priceAtoB: 0, priceBtoA: 0});
		else {
			const currentPrice = await getCurrentPrice(lpToken, amount);	
			setPrice(currentPrice);
		}
	}

	const getLiquidity = async (amount, atob) => {
		if(lpToken === ZERO_ADDRESS) setTokenBAmount(0);
		else {
			const liquidityData = await getLiquidityData(lpToken, amount, tokenBDirection);
			const amountB = atob ? liquidityData.liquidityAtoB : liquidityData.liquidityBtoA;
			setTokenBAmount(amountB);
			const estimateLpTokens = getEstimateLpTokens(amount, amountB);
			setEstimateLpTokens(toWei(estimateLpTokens.toString()));
		}
	}

	useEffect(() => {
		getLiquidity(tokenAAmount, true);
	},[lpToken, tokenAAmount]);

	useEffect(() => {
		getPrice(1);
	}, [lpToken]);

	const darkBg = "#14245a";
	const bg = "#243777";
	const pink = "#ff01b4";
	const lightPink = "#ff01b4";

	const showError = (msg) => {
		toast({
			title: intl.get("error"),
			description: msg,
			status: "error",
			duration: 4000,
			isClosable: true
		})
	}

	const showSuccess = (msg) => {
		toast({
			title: intl.get("success"),
			description: msg,
			status: "success",
			duration: 4000,
			isClosable: true
		})
	}

	const loadData = async () => {
		if(currentAccount === undefined) return;
		let _spToken;
		let _tokenB;

		const controller = new AbortController();
		const signal = controller.signal;

		let url = `${BASE_API_URL}/items/get_liquidity_addable_nfts/${CHAIN_ID}/${NFT_ADDRESS}`;

		let res = await fetch(url, {
			method: "get",
			headers: { 'Content-Type': 'application/json' },
			signal,
		})
		let data = await res.json();
		let liquidityAddableNfts = [];
		if(data.success) {
			liquidityAddableNfts = data.list.map((item, index) => item.tokenId);
		}

		if(spTokens.length === 0) {
			_spToken = await getAllSPTokens();
			let availableSPTokens = [];
			for(let i = 0; i < _spToken.length; i++) {
				const _tokenId = _spToken[i].symbol.substring(_spToken[i].symbol.indexOf('#') + 1);
				if(liquidityAddableNfts.includes(parseInt(_tokenId))) availableSPTokens.push(_spToken[i]);
			}
			// Filter by liquidityAddable from nfts database, if spToken's id is not liquidityAddable, don't show in the list
			setSPTokens(availableSPTokens);
		} else {
			_spToken = spTokens;
		}
		if(tokenBs.length === 0) {
			_tokenB = await getTokenBs(); 
			setTokenBs(_tokenB);			
		} else {
			_tokenB = tokenBs;
		}

		if(_spToken.length > 0) {
			setTokenABalance(await getBalance(_spToken[tokenAId].address, currentAccount));
			setTokenAAddress(_spToken[tokenAId].address);
			setTokenASymbol(_spToken[tokenAId].symbol);
			setSlippage(await getSlippage(_spToken[tokenAId]));
			setSptAmountInPool(await getBalance(_spToken[tokenAId].address, SPTOKEN_VAULT));
		}
		if(_tokenB.length > 0) {
			setTokenBBalance(await getBalance(_tokenB[tokenBId].address, currentAccount));
			setTokenBAddress(_tokenB[tokenBId].address);
			setTokenBSymbol(_tokenB[tokenBId].symbol);
			setTokenBDirection(_tokenB[tokenBId].direction);
		}
		if(_spToken.length > 0 && _tokenB.length > 0) {
			const __lpToken = await getPair(_spToken[tokenAId].address, _tokenB[tokenBId].address);
			setLpAmountInPool(await getLpAmountInPool(__lpToken));
			setLpAmountOfMine(await getDappLpAmountOfMine(__lpToken, currentAccount));	
			setLpToken(__lpToken);
			setMyPotentialBonus(await getPotentialSPTokenBonus(_spToken[tokenAId].address, _tokenB[tokenBId].address, currentAccount));
		}
		setMyLPTokens(await getMyLPtokens(currentAccount, _spToken, _tokenB));
	}

	const _addLiquidity = async () => {
		if(currentAccount === undefined) showError(intl.get("please_log_in"));
		setHiddenConfirmationProgress(false);
		setAddLiquidityButtonAvailable(false);
		setConfirmationProgressData({step: '1/5', value: 20, message: intl.get("start")});
		try {
			// Check tokenA allowance
			const allowanceA = await getAllowance(tokenAAddress, currentAccount, SPTOKEN_VAULT);
			// console.log(tokenAAmount.toString(), allowanceA.toString(), allowanceA.lt(toWei(tokenAAmount.toString())));

			if(allowanceA.lt(toWei(tokenAAmount.toString()))) {
				// Aprove tokenA if allowance is not enough
				setConfirmationProgressData({step: '2/5', value: 40, message: intl.get("approve_token") + tokenASymbol});
				const tx = await approve(tokenAAddress, SPTOKEN_VAULT, toWei(tokenAAmount.toString()));
				await tx.wait();
			}

			// Check tokenB allowance
			const allowanceB = await getAllowance(tokenBAddress, currentAccount, SPTOKEN_VAULT);
			// console.log(tokenBAmount.toString(), allowanceB.toString());
			// console.log(allowanceB, toWei(tokenBAmount.toString()));
			if(allowanceB.lt(toWei(tokenBAmount.toString()))) {
				// Aprove tokenB if allowance is not enough
				setConfirmationProgressData({step: '3/5', value: 60, message: intl.get("approve_token") + tokenBSymbol});
				const tx = await approve(tokenBAddress, SPTOKEN_VAULT, toWei(tokenBAmount.toString()));
				await tx.wait();
			}

			// Run add liquidity
			setConfirmationProgressData({step: '4/5', value: 80, message: intl.get("adding_liquidity") + ": " + tokenASymbol + "/" + tokenBSymbol});

			const tx = await addLiquidity(tokenAAddress, tokenBAddress, toWei(tokenAAmount.toString()), toWei(tokenBAmount.toString()), frozenTime);
			await tx.wait(1);
			setConfirmationProgressData({step: '5/5', value: 100, message: intl.get("add_liquidity_done") + ": " + tokenASymbol + "/" + tokenBSymbol});
			showSuccess(intl.get("add_liquidity_done"));

			setTimeout(async () => {
				setHiddenConfirmationProgress(true);
				setAddLiquidityButtonAvailable(true);
				await loadData();
			}, 4000);
		} catch (err) {
			showError(err.message);
			setHiddenConfirmationProgress(true);
			setAddLiquidityButtonAvailable(true);
		}
	}

	const _removeLiquidity = async () => {
		if(currentAccount === undefined) showError(intl.get("please_log_in"));
		setHiddenConfirmationProgress(false);
		setRemoveLiquidityButtonAvailable(false);
		setConfirmationProgressData({step: '1/2', value: 20, message: intl.get("start")});
		try {
			const tx = await removeLiquidity(tokenAAddress, tokenBAddress);
			await tx.wait();
			setConfirmationProgressData({step: '2/2', value: 100, message: intl.get("removing_liquidity")});
			showSuccess(intl.get("claim_success"));

			setTimeout(async () => {
				setHiddenConfirmationProgress(true);
				setRemoveLiquidityButtonAvailable(true);
				await loadData();
			}, 4000);	
		} catch (err) {
			showError(err.message);
			setHiddenConfirmationProgress(true);
			setRemoveLiquidityButtonAvailable(true);
		}
	}

	const onSPTokenSelectChange = async (id) => {
		const spToken = spTokens[id];
		setTokenAId(id);
		setTokenAAddress(spToken.address);
		setTokenASymbol(spToken.symbol);
		setTokenABalance(await getBalance(spToken.address, currentAccount));

		const _lpToken = await getPair(spToken.address, tokenBAddress);
		setLpAmountInPool(await getLpAmountInPool(_lpToken));
		setLpAmountOfMine(await getDappLpAmountOfMine(_lpToken, currentAccount));
		setLpToken(_lpToken);
		setSlippage(await getSlippage(spToken.address));
		setSptAmountInPool(await getBalance(spToken.address, SPTOKEN_VAULT));
		setMyPotentialBonus(await getPotentialSPTokenBonus(spToken.address, tokenBAddress, currentAccount));
	}

	const onTokenBSelectChange = async (id) => {
		const tokenB = tokenBs[id];
		setTokenBId(id);
		setTokenBAddress(tokenB.address);
		setTokenBSymbol(tokenB.symbol);
		setTokenBDirection(tokenB.direction);
		setTokenBBalance(await getBalance(tokenB.address, currentAccount));

		const _lpToken = await getPair(tokenAAddress, tokenB.address);
		setLpAmountInPool(await getLpAmountInPool(_lpToken));
		setLpAmountOfMine(await getDappLpAmountOfMine(_lpToken, currentAccount));
		setLpToken(_lpToken);
	}

	const getEstimateLpTokensRate = () => {
		if(lpAmountOfMine === undefined) return 0;
		const rate = estimateLpTokens / (estimateLpTokens.add(lpAmountInPool));
		// console.log(estimateLpTokens.toString(), lpAmountInPool.toString(), rate.toString());
		return rate;
	}

	return(
		<Box>
		{currentAccount === undefined ? <></> :
		<Box mt={5}>
			<Box 
				mt={10} 
				w={["90%", "26%"]} 
				ml={["5%", "37%"]} 
				color={"#ddd"} 
				borderRadius={20} 
				bg={darkBg}
				p={[4,6]}
			>
				<Box fontSize={"lg"} textAlign="center" mb={5} color={"white"} textTransform={"uppercase"}>{intl.get("liquidity")}</Box>

				{/* SPT selector */}
				<Box 
					w={"100%"}
					bg={bg}
					borderRadius={15}
					p={3} 
				>
					<Box align="right" mr={3} mb={2} fontSize={"sm"}>{intl.get("balance")}: {format(fromWei(tokenABalance.toString()), 2)}</Box>
					<Box display={"flex"}>
					<InputGroup>
						<Input 
							borderWidth={0} 
							fontWeight={600} 
							size={"md"} 
							w={"50%"} 
							borderColor={bg} 
							focusBorderColor={bg} 
							value={tokenAAmount} 
							placeholder={"amount to add"}
							onChange={(e) => setTokenAAmount(e.target.value)}></Input>
						<InputRightElement w={"50%"}>

						<Select borderWidth={0} size={"md"} borderColor={bg} focusBorderColor={bg} onChange={(e) => onSPTokenSelectChange(e.target.value)}>
							{spTokens.length === 0 ? <></> : spTokens.map((v, i) => 
								<option key={i} value={i}>{v.symbol + ` (${shortAddress(v.address)})`}</option>)}
						</Select>

						{/* ###### */}
						</InputRightElement>
					</InputGroup>
					</Box>
				</Box>

				<Box textAlign="center" fontSize="lg" mt={3} mb={3}>+</Box>

				{/* TokenB selector */}
				<Box 
					w={"100%"}
					bg={bg}
					borderRadius={15}
					p={3} 
				>
					<Box align="right" mr={3} mb={2} fontSize={"sm"}>{intl.get("balance")}: {format(fromWei(tokenBBalance.toString()), 2)}</Box>
					<Box display={"flex"}>
					<InputGroup>
						<Input 
							borderWidth={0} 
							fontWeight={600} 
							size={"md"} 
							w={"50%"} 
							borderColor={bg} 
							focusBorderColor={bg} 
							value={lpToken === ZERO_ADDRESS ? tokenBAmount : format(tokenBAmount, 5)} 
							placeholder={lpToken === ZERO_ADDRESS ? "initial amount to add" : ""}
							readOnly={lpToken !== ZERO_ADDRESS} 
							onChange={(e) => {if(lpToken === ZERO_ADDRESS) setTokenBAmount(e.target.value)}}></Input>
						<InputRightElement w={"50%"}>
							<Select
								borderWidth={0} size={"md"} borderColor={bg} focusBorderColor={bg} 
								onChange={(e) => onTokenBSelectChange(e.target.value)}>
								{tokenBs.length === 0 ? <></> : tokenBs.map((v, i) => <option key={i} value={i}>{v.symbol + ` (${shortAddress(v.address)})`}</option>)}
							</Select>
						</InputRightElement>
					</InputGroup>
					</Box>
				</Box>

				{/* Frozen time selector */}
				<Box w={"100%"} bg={bg} borderRadius={15} p={3} mt={5}>
					<Select
					borderWidth={0} size={"md"} borderColor={bg} focusBorderColor={bg} 
					onChange={(e) => setFrozenTime(e.target.value)}>
						{frozenTimes.map((v, i) => <option key={i} value={v.value}>{v.label}</option>)}
					</Select>
				</Box>

				{/* Current exchange rate */}
				{lpToken === ZERO_ADDRESS ? 
				<Box
					mt={5}
					w={"100%"}
					borderColor={bg}
					borderRadius={15}
					borderWidth={2}
					p={3}
					display={"flex"}
					fontSize={"sm"}
				>
					You are the first liquidity provider.<br/>
					The ratio of tokens you add will set the price of this pool.<br/>
					Once you are happy with the rate click bellow button.
				</Box> :
				<Box>
					<Box ml={1} mt={5} fontSize="sm" mb={2} color={lightPink} textTransform="uppercase">{intl.get("current_exchange_rate")}</Box>
					<Box
						w={"100%"}
						borderColor={bg}
						borderRadius={15}
						borderWidth={2}
						p={3}
						display={"flex"}
						fontSize={"sm"}
					>
						<Box w={"33%"} textAlign="center">
							<Box mb={2} fontWeight={600}>{format(fromWei((tokenBDirection ? price.priceBtoA : price.priceAtoB).toString()), 5)}</Box>
							<Box color={lightPink}>{tokenASymbol} per {tokenBSymbol}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2} fontWeight={600}>{format(fromWei((tokenBDirection ? price.priceAtoB : price.priceBtoA).toString()), 5)}</Box>
							<Box color={lightPink}>{tokenBSymbol} per {tokenASymbol}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2} fontWeight={600}>{format(getEstimateLpTokensRate() * 100, 2) + "%"}</Box>
							<Box color={lightPink}>{intl.get("estimated_lp_share")}</Box>
						</Box>
					</Box>
				</Box>}

				{/* Add liquidity button */}
				<Button disabled={!addLiquidityButtonAvailable} size={"lg"} mt={5} mb={5} w={"100%"} bg={pink} color={"white"} onClick={() => _addLiquidity()}>
					{intl.get("add_liquidity")}
				</Button>

				{/* LP token balance, and remove liquidity */}
				<Box ml={1} fontSize="sm" mb={2} color={lightPink} textTransform="uppercase">{intl.get("liquidity_pool_data")}</Box>
				<Box
					w={"100%"}
					borderColor={bg}
					borderRadius={15}
					borderWidth={2}
					p={5}
					fontSize={"sm"}
				>
					<Box display={"flex"}>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{format(fromWei(lpAmountInPool.toString()), 2)}</Box>
							<Box color={lightPink}>{intl.get("pool_lp_balance")}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{format(fromWei(lpAmountOfMine.amount.toString()), 2)}</Box>
							<Box color={lightPink}>{intl.get("my_lp_balance")}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{lpAmountOfMine.amount * 1 === 0 ? '-' : format(lpAmountOfMine.amount / lpAmountInPool * 100, 2) + '%'}</Box>
							<Box color={lightPink}>{intl.get("share_of_pool")}</Box>
						</Box>
					</Box>
					<Divider mt={"5"} mb={"5"} color={bg}/>
					<Box display={"flex"}>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{sptAmountInPool * 1 === 0 ? '0' : format(fromWei(sptAmountInPool.toString()), 2)}</Box>
							<Box color={lightPink}>{intl.get("pool_spt_balance")}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{myPotentialBonus[0] * 1 === 0 ? '0*' : format(fromWei(myPotentialBonus[0].toString()), 2)}</Box>
							<Box color={lightPink}>{intl.get("my_estimate_bonus")}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>-</Box>
							<Box color={lightPink}>{intl.get("to_next_bonus")}</Box>
						</Box>
					</Box>
					<Divider mt={"5"} mb={"5"} color={bg}/>
					<Box display={"flex"}>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{lpAmountOfMine.amountA * 1 === 0 ? '0' : format(fromWei(lpAmountOfMine.amountA.toString()), 2)}</Box>
							<Box color={lightPink}>{intl.get("provide_amount") + tokenASymbol}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{lpAmountOfMine.amountB * 1 === 0 ? '0' : format(fromWei(lpAmountOfMine.amountB.toString()), 2)}</Box>
							<Box color={lightPink}>{intl.get("provide_amount") + tokenBSymbol}</Box>
						</Box>
						<Box w={"33%"} textAlign="center">
							<Box mb={2}>{lpAmountOfMine.frozenTime * 1 === 0 || lpAmountOfMine.amount * 1 === 0 ? '-' : date.format(new Date(lpAmountOfMine.frozenTime.mul(1000).toNumber()), "YY-MM-DD HH:mm")}</Box>
							<Box color={lightPink}>{intl.get("frozen_until")}</Box>
						</Box>
					</Box>

					{/* Remove liquidity button */}
					<Button disabled={!removeLiquidityButtonAvailable} size={"lg"} mt={5} w={"100%"} bg={pink} color={"white"} onClick={() => _removeLiquidity()}>
						{intl.get("remove_liquidity")}
					</Button>

					{/* Reason of zero bonus */}
					{myPotentialBonus[0] * 1 === 0 && sptAmountInPool * 1 > 0 ? 
					<Box mt={3}>
						{'*Reason of zero bonus: ' + myPotentialBonus[1]}
					</Box> : <></>}

				</Box>

				{hiddenConfirmationProgress ? <></> :
				<Box mt={3} color={"white"} w={"90%"} ml={"5%"}>
					<ConfirmationProgress 
						step={confirmationProgressData.step}
						value={confirmationProgressData.value}
						message={confirmationProgressData.message}
					/>
				</Box>}

				{/* Show addresses of LP token and SP token */}
				<Box mt={5}>
					{lpToken === ZERO_ADDRESS ? <></> :
					<Box fontSize={"sm"} textAlign="center" mt={3}>
						<Link href={ETHER_SCAN_URL + "address/" + lpToken} isExternal>
							Liquidity Proof: {shortAddress(lpToken)}
						</Link>
					</Box>}
					<Box fontSize={"sm"} textAlign="center" mt={3}>
						<Link href={ETHER_SCAN_URL + "address/" + tokenAAddress} isExternal>
							{tokenASymbol}: {shortAddress(tokenAAddress)}
						</Link>
					</Box>
					<Box fontSize={"sm"} textAlign="center" mt={3}>
						<Link href={ETHER_SCAN_URL + "address/" + SPTOKEN_VAULT} isExternal>
							Vault: {shortAddress(SPTOKEN_VAULT)}
						</Link>
					</Box>
					<Box fontSize={"sm"} textAlign="center" mt={3}>
						{`Slippage: ${slippage.allocationRate / 100 + "% + " + slippage.lpBonusRate / 100 + "%"}`}
					</Box>
				</Box>

			</Box>

			{/* All LPToken in wallet */}
			<Box 
				mt={5} 
				w={["90%", "26%"]} 
				ml={["5%", "37%"]} 
				borderRadius={20} 
				bg={darkBg}
				p={[4,6]}
			>
				<Box ml={1} fontSize="sm" mb={2} color={lightPink} textTransform="uppercase">{intl.get("lp_tokens_in_your_wallet")}</Box>
				<Box
					w={"100%"}
					borderColor={bg}
					borderRadius={15}
					borderWidth={2}
					pl={[4,7]}
					pr={[4,7]}
					pb={0}
					pt={8}
					fontSize={"sm"}
				>
				{myLPTokens.length === 0 ? <Box	color={"#ddd"} mb={5} fontSize="sm">None</Box> :
				myLPTokens.map((v, i) =>
				<Box key={i}>
					<Box display={"flex"} fontSize="sm" mb={3} color={'#ddd'}>
						<Box w={"50%"} fontWeight={300}>
							{"LP for " + v.symbol}
							<br/>
							<Link href={ETHER_SCAN_URL + "address/" + v.address} isExternal>{shortAddress(v.address)}</Link>
						</Box>
						<Box w={"50%"} textAlign="right" fontWeight={300} fontSize="sm" mt={-3}>
							{format(fromWei(v.balance.toString()), 2) + " LP"}
							<br/>
							{v.symbolA === undefined ? <></> : format(fromWei(v.amountA.toString()), 2) + " " + v.symbolA}
							<br/>
							{v.symbolB === undefined ? <></> : format(fromWei(v.amountB.toString()), 2) + " " + v.symbolB}
						</Box>
					</Box>
					{i === myLPTokens.length - 1 ? <></> : <Divider mb={"6"} color={bg} />}
				</Box>
					)}

				</Box>
			</Box>
		</Box>}
		</Box>
	)
}

export default LiquidityMain;